import omit from 'lodash/omit';

import { JsonSchemaEnum } from '@evidentid/json-schema/interfaces/JsonSchema';
import { Producer } from '@evidentid/tprm-portal-lib/models/dashboard';

import { resolveEnumValue } from '@/utils/enum-labels/enumLabels';

/** Lets a file name a producer's coverage type by its label as well as its key, as enum columns
 * do. A name that is neither stays as written, so validation sends the row for correction. */
export function withResolvedCoverageTypes<T extends { producerList?: Partial<Producer>[] }>(
    entity: T, coverageTypeSchema: JsonSchemaEnum,
): T {
    if (!entity.producerList) {
        return entity;
    }
    return {
        ...entity,
        producerList: entity.producerList.map((producer) => (Array.isArray(producer.coverageTypes)
            ? {
                ...producer,
                coverageTypes: producer.coverageTypes.map(
                    (value) => resolveEnumValue(coverageTypeSchema, String(value)),
                ),
            }
            : producer)),
    };
}

/** Completes producers read from a file into the API shape. A producer with coverage types is active. */
export function toImportedProducers(producers: Partial<Producer>[]): Producer[] {
    return producers.map((producer) => {
        const coverageTypes = producer.coverageTypes || [];
        return {
            producerFirm: producer.producerFirm || '',
            contactName: producer.contactName || '',
            email: producer.email || '',
            phone: producer.phone || '',
            isActive: coverageTypes.length > 0,
            coverageTypes,
            isDefault: Boolean(producer.isDefault),
        };
    });
}

/** With a Producers column, a row's cell replaces the entity's producers, and an empty cell removes them.
 * Without one, `producerList` is left out so the entity keeps the producers it has. */
export function withImportedProducers<T extends { producerList?: Partial<Producer>[] }>(
    entity: T, fileHasProducersColumn: boolean,
): T {
    return fileHasProducersColumn
        ? { ...entity, producerList: toImportedProducers(entity.producerList || []) } as T
        : omit(entity, 'producerList') as T;
}
